import dayjs from "@/lib/helpers/dayjs";
import { Divider2 } from "@/ui2/atoms/Divider";
import type { PatientDetails, PatientStatus } from "@/core/types";

import { PatientData } from "../../components/PatientData";

type Props = {
  patientDetails: PatientDetails;
};

const HIDDEN_STATUSES = new Set<PatientStatus>(["ASSESSMENT", "REFERRED", "REGISTERED"]);

export function PatientStatusDetails({ patientDetails }: Props) {
  const {
    inactivation_reason,
    inactivation_type,
    is_wait_listed,
    number_of_days_in_care,
    status,
    wait_list_comment,
    wait_list_reason,
    wait_listed_date,
  } = patientDetails;

  const isRevoked = inactivation_type === "ACCESS_REVOKED";
  const isRateLimited = inactivation_type === "RATE_LIMIT";
  const isInactive = status === "INACTIVE";

  const isInactivationShown = isInactive && (isRevoked || isRateLimited);
  const isCareDaysCountShown = !HIDDEN_STATUSES.has(status) && !isInactivationShown;

  if (!isCareDaysCountShown && !isInactivationShown && !is_wait_listed) return null;

  return (
    <dl className="tw:flex tw:flex-col tw:gap-1 tw:text-sm tw:text-text-tertiary tw:lg:pb-4">
      {isCareDaysCountShown && (
        <div>
          <dt className="tw:sr-only">Days in care</dt>
          <dd>Day {number_of_days_in_care} of care</dd>
        </div>
      )}

      {isInactivationShown && (
        <div className="tw:flex">
          <dt>{getInactivationTerm(isRevoked)}:</dt>
          <dd className="tw:ml-ch">{inactivation_reason}</dd>
        </div>
      )}

      {is_wait_listed && (
        <>
          <div>
            <dt className="tw:sr-only">Waitlist</dt>
            <dd>{getDaysOnWaitList(wait_listed_date ?? 0)}</dd>
          </div>

          <div className="tw:flex">
            <dt>Reasons:</dt>
            <dd className="tw:ml-ch">{getWaitListReasons(wait_list_reason ?? "")}.</dd>
          </div>

          {wait_list_comment && (
            <>
              <Divider2 className="tw:mt-3! tw:-mr-4" />
              <PatientData className="tw:pt-3 tw:text-text-primary" label="Comment">
                {wait_list_comment}
              </PatientData>
            </>
          )}
        </>
      )}
    </dl>
  );
}

function getInactivationTerm(isRevoked: boolean): string {
  return isRevoked ? "Access Revoked" : "Inactivation Reason";
}

function getDaysOnWaitList(waitListedDate: number): string {
  const daysOnWaitList = dayjs().diff(dayjs.unix(waitListedDate), "days");
  const count = isNaN(daysOnWaitList) ? 0 : daysOnWaitList;
  const dayText = count === 1 ? "day" : "days";

  return `${count} ${dayText} on a waitlist.`;
}

function getWaitListReasons(waitListReason: string | string[]): string {
  return Array.isArray(waitListReason) ? waitListReason.join(", ") : waitListReason;
}
